<template>
  <gc-main-layout
    :title="title"
    :isXSmall="isXSmall"
    :isLarge="isLarge"
    :baseView="baseView"
    :key="pageKey"
    :tabs="tabs"
    :selectedIndex="selectedIndex"
    :backButtonEnable="true"
    :backButtonFunction="backButtonFunction"
  >
    <template #headerContent>
      <GcHeaderContentDynamic
        title="Supplier"
        :baseView="baseView"
        :primaryLabel="model.Name"
        :secondaryLabel="baseView.id"
        recordType="Supplier"
        v-if="loaded"
      />
    </template>

    <div class="css-v2 transaction-list">
      <div class="data-grid-details">
        <GcDataGrid
          :grouping="true"
          :export="true"
          :summary="true"
          :dataSourceConfig="
            screenJson.gridData
              ? screenJson.gridData.gridOptions.dataSource
              : undefined
          "
          :columns="getColumns"
          :gridToolbar="
            screenJson.gridData ? screenJson.gridData.toolbar : undefined
          "
          :id="'supplier-product-list-grid'"
          v-if="loaded && screenJson.gridData"
          ref="supplierProductListGrid"
          :isEditableGrid="false"
          :showFooter="false"
          :gridOptions="
            screenJson.gridData ? screenJson.gridData.gridOptions : undefined
          "
          :heightPadding="20"
          :persistenceKey="persistenceKey"
          :saveGridLayout="true"
          :key="gridKey"
        >
        </GcDataGrid>
      </div>
    </div>

    <gcPopupSaveLayout
      :visible.sync="getVisibleGridSavePopup"
      :persistenceKey="persistenceKey"
      :showLoading="setLoading"
      :original-columns="[...deepCloneColumns]"
      v-if="getVisibleGridSavePopup"
    />
  </gc-main-layout>
</template>

<script>
import RouteMixin from "@/_mixins/route.mixin";
import FormMixin from "@/_mixins/form.mixin";
import DataGridMixin from "@/_mixins/data-grid.mixin";
import { SupplierService } from "../../_services/api/suppliers/supplier.service";
import { CommonStorage } from "../../_services/storage";
import { DataGridState } from "../../_models/data-grid-state";
import gcPopupSaveLayout from "../../popups/data-grid/gc-popup-save-layout.vue";
import { mapGetters } from "vuex";
import DataGridHelpers from "@/_helpers/data-grid/helpers";
import ColumnWidth from "../../_helpers/data-grid/ColumnWidth";
const { syncDateFilters } = DataGridHelpers;

export default {
  mixins: [RouteMixin, FormMixin, DataGridMixin],
  components: {
    gcPopupSaveLayout,
  },
  data() {
    return {
      persistenceKey: "supplier-product-list",
      model: {
        Code: "",
        Name: "",
      },
      loaded: false,
      backButtonRoute: "/supplier",
      gridKey: new Date().getTime(),
    };
  },
  async beforeMount() {
    const removeState = CommonStorage.getItems("remove_state");

    if (this.persistenceKey) {
      DataGridState.initializeGridViewDefinition(this.persistenceKey, [
        ...this.getColumns,
      ]);
    } else if (removeState === "true") {
      await DataGridState.removeState(this.persistenceKey);
    }
    CommonStorage.saveItems("remove_state", false);
  },
  computed: {
    ...mapGetters({ visibleGridSavePopup: "ui/getVisibleGridSavePopup" }),
    ...mapGetters({
      getTriggerViewDefinitionChange:
        "gridViewDefinition/getTriggerViewDefinitionChange",
    }),
    ...mapGetters({
      getSelectedDefinitionByKey:
        "gridViewDefinition/getSelectedDefinitionByKey",
    }),
    tabs() {
      return [
        {
          text: "Details",
          path: `/supplier/review/${this.baseView.id}`,
          applicationFeatureCode: "Customer",
        },
        {
          text: "Contacts",
          path: `/supplier-contact/${this.baseView.id}`,
          applicationFeatureCode: "Customer",
        },
        {
          text: "Notes",
          path: `/supplier-note/${this.baseView.id}`,
          applicationFeatureCode: "Customer",
        },
        {
          text: "Products",
          path: `/supplier-product/${this.baseView.id}`,
          applicationFeatureCode: "Product",
        },
        {
          text: "Transactions",
          path: `/supplier-transaction-history/${this.baseView.id}`,
          applicationFeatureCode: "SupplierTransactionHistory",
        },
      ];
    },
    screenJson() {
      return {
        title: "Suppliers",
        exportFileName: "Supplier Products List",
        gridData: {
          gridOptions: {
            dataSource: {
              endpoint: "ProductPurchaseListWithProductDetail",
              // filter: [["SupplierList", "contains", this.baseView.id]],
              filter: [["SupplierCode", "=", this.baseView.id]],
              serverPaging: true,
              allowGrouping: false,
              keyType: {
                Attribute01: "Guid",
                Attribute02: "Guid",
                Attribute03: "Guid",
                Attribute04: "Guid",
                Attribute05: "Guid",
                Attribute06: "Guid",
                Attribute07: "Guid",
                Attribute08: "Guid",
                Attribute09: "Guid",
                Attribute10: "Guid",
                Attribute11: "Guid",
                Attribute12: "Guid",
                Attribute13: "Guid",
                Attribute14: "Guid",
                Attribute15: "Guid",
                Attribute16: "Guid",
                Attribute17: "Guid",
                Attribute18: "Guid",
                Attribute19: "Guid",
                Attribute20: "Guid",
              },
            },
            noDataMessage: {
              addNew: {
                class: "add-new",
                title: "No Products",
                message: "No products found for this supplier.",
              },
            },
          },
          toolbar: {
            search: false,
            columnChooser: true,
            export: true,
            openFilter: true,
            openFilterByDefault: true,
            expand: false,
            filterOnEnter: true,
          },
          columns: this.getColumns,
        },
      };
    },
    getColumns() {
      return [
        {
          screenField: "Item",
          label: "Product",
          gridDataField: "Description1",
          sortOrder: "asc",
          sortIndex: 0,
          allowHiding: false,
          cellTemplateRecord: {
            primary: [{ dataField: "Description1" }],
            secondary: [{ dataField: "ProductCode" }],
          },
          exportFields: [
            { dataField: "ProductCode", label: "Item Code" },
            { dataField: "Description1", label: "Item Description 1" },
            { dataField: "Description2", label: "Item Description 2" },
            { dataField: "Description3", label: "Item Description 3" },
          ],
          fixed: true,
          fixedPosition: "left",
        },
        {
          dataField: "UnitOfMeasureCode",
          label: "Stock UM",
          width: ColumnWidth.UnitOfMeasure.minWidth,
        },
        {
          label: "Supplier Item",
          dataField: "SupplierItem",
          width: 120,
          gcType: "String",
        },
        {
          dataField: "SupplierUM",
          label: "Sup UM",
          width: ColumnWidth.UnitOfMeasure.minWidth,
        },
        {
          label: "Conversion",
          dataField: "SupplierConversion",
          type: "Decimal",
          format: "Decimal-16-4",
          minWidth: 80,
          width: 80,
        },
        {
          label: "Supplier Price",
          dataField: "SupplierPrice",
          type: "Decimal",
          format: "Decimal-16-4",
          minWidth: ColumnWidth.Price4DecimalPoints.minWidth,
          width: ColumnWidth.Price4DecimalPoints.width,
        },
        {
          label: "Currency",
          dataField: "CurrencyCode",
          minWidth: ColumnWidth.CurrencyCode.minWidth,
          width: ColumnWidth.CurrencyCode.width,
        },
        {
          label: "Lead Days",
          dataField: "SupplierLead",
          minWidth: 80,
          width: 80,
        },
        {
          label: "Planning %",
          dataField: "PlanningPct",
          type: "EditableDecimal",
          format: "Decimal-16-4",
          minWidth: ColumnWidth.Price2DecimalPoints.minWidth,
          width: ColumnWidth.Price2DecimalPoints.width,
        },
        {
          label: "Duty %",
          dataField: "DutyRate",
          type: "EditableDecimal",
          format: "Decimal-16-4",
          minWidth: ColumnWidth.Price2DecimalPoints.minWidth,
          width: ColumnWidth.Price2DecimalPoints.width,
        },
        {
          label: "Freight %",
          dataField: "FreightLandingRate",
          type: "EditableDecimal",
          format: "Decimal-16-4",
          minWidth: ColumnWidth.Price2DecimalPoints.minWidth,
          width: ColumnWidth.Price2DecimalPoints.width,
        },
        {
          label: "Landing Cost %",
          dataField: "LandedCostFactor",
          type: "EditableDecimal",
          format: "Decimal-16-4",
          minWidth: ColumnWidth.Price2DecimalPoints.minWidth,
          width: ColumnWidth.Price2DecimalPoints.width,
        },
        {
          label: "Variable",
          width: 90,
          cellTemplate: "CheckBoxTemplate",
          makeReadOnly: () => true,
          dataField: "IsVariable",
          alignment: "center",
        },
        {
          label: "Preferred",
          dataField: "IsDefault",
          width: 90,
          cellTemplate: "CheckBoxTemplate",
          makeReadOnly: () => true,
          alignment: "center",
        },
        {
          screenField: "Name",
          filterLabel: "Product",
          filterDataField: "ProductSearchText",
          showInColumnChooser: false,
          visible: false,
          type: "Text",
          filterIndex: 1,
        },
        {
          screenField: "ProductCategory1",
          label: "Category 1",
          filterLabel: "Category 1",
          filterIndex: 7,
          visible: false,
          showInColumnChooser: false,
        },
        {
          screenField: "ProductCategory2",
          label: "Category 2",
          filterLabel: "Category 2",
          filterIndex: 8,
          visible: false,
          showInColumnChooser: false,
        },
        {
          screenField: "ProductCategory3",
          label: "Category 3",
          filterLabel: "Category 3",
          filterIndex: 9,
          visible: false,
          showInColumnChooser: false,
        },
        {
          screenField: "ProductCategory4",
          label: "Category 4",
          filterLabel: "Category 4",
          filterIndex: 10,
          visible: false,
          showInColumnChooser: false,
        },
        {
          screenField: "ProductType",
          label: "Type",
          filterLabel: "Type",
          filterIndex: 11,
          visible: false,
          showInColumnChooser: false,
        },
        {
          screenField: "ProductSize",
          label: "Size",
          filterLabel: "Size",
          filterIndex: 12,
          visible: false,
          showInColumnChooser: false,
        },
        {
          screenField: "BatchMethod",
          visible: false,
          showInColumnChooser: false,
          filterIndex: 4,
          width: 100,
        },
        {
          screenField: "ProductSource",
          visible: false,
          filterIndex: 5,
          filterDataField: "Source",
          width: 100,
        },
        {
          screenField: "AvailableForSale",
          visible: false,
          filterIndex: 6,
          filterDataField: "AvailableForSale",
          width: 120,
        },
      ]
        .concat(this.getAttributeColumns(12))
        .concat({
          screenField: "Status",
          gridDataField: "Inactive",
          gridLabel: "Status",
          filterIndex: 2,
          filterDefaultValue: ["N"],
          visible: false,
          showInColumnChooser: false,
        });
    },
    getVisibleGridSavePopup: {
      get() {
        return this.visibleGridSavePopup;
      },
      set(val) {
        this.$store.dispatch("ui/setVisibleGridSavePopup", val);
      },
    },
    deepCloneColumns() {
      return JSON.parse(JSON.stringify(this.getColumns));
    },
  },
  methods: {
    setLoading(val) {
      this.showLoading(val);
    },
    backButtonFunction() {
      this.$router.push({ path: this.backButtonRoute });
    },
  },
  async created() {
    this.selectedIndex = 3;
    await SupplierService.getByCode(this.baseView.id).then((response) => {
      if (response.data.value.length > 0) {
        this.model.Code = response.data.value[0].Code;
        this.model.Name = response.data.value[0].Name;
        this.refreshView();
      }
    });
    this.loaded = true;
  },
  watch: {
    getTriggerViewDefinitionChange: {
      handler(value) {
        if (value) {
          this.$store.dispatch(
            "gridViewDefinition/setTriggerViewDefinitionChange",
            false,
          );

          const selectedDefinition = this.getSelectedDefinitionByKey(
            this.persistenceKey,
          );

          if (selectedDefinition) {
            if (selectedDefinition.GridDefinition) {
              const GridDefinition = JSON.parse(
                selectedDefinition.GridDefinition,
              );
              if (GridDefinition) {
                CommonStorage.saveItems(
                  this.persistenceKey,
                  JSON.stringify(GridDefinition),
                );
              }
            }

            if (selectedDefinition.FilterDefinition) {
              const FilterDefinition = JSON.parse(
                selectedDefinition.FilterDefinition,
              );
              if (FilterDefinition) {
                const syncedFilterDefinition = syncDateFilters(
                  FilterDefinition,
                  this.deepCloneColumns,
                );
                CommonStorage.saveItems(
                  "filter-panel-" + this.persistenceKey,
                  JSON.stringify(syncedFilterDefinition),
                );
              }
            } else {
              CommonStorage.clear("filter-panel-" + this.persistenceKey);
            }

            this.$nextTick(() => {
              if (this.$refs["supplierProductListGrid"]) {
                this.gridKey = new Date().getTime();
              }
            });
          }
        }
      },
      deep: true,
    },
  },
};
</script>

<style lang="scss"></style>
